import type { ReactNode } from "react";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { cn } from "src/lib/cn";
import InlineSVG from "react-inlinesvg";

type BottomActionToastAction = {
  label: string;
  onClick: () => void;
  variant?: "primary" | "tertiary";
  disabled?: boolean;
  ariaLabel?: string;
};

type BottomActionToastProps = {
  message: ReactNode;
  actions?: BottomActionToastAction[];
  onDismiss?: () => void;
  dismissLabel?: string;
  className?: string;
};

export const BottomActionToast = ({
  message,
  actions = [],
  onDismiss,
  dismissLabel = "Dismiss",
  className,
}: BottomActionToastProps) => {
  return (
    <div
      className={cn("w-full rounded-2xl border border-solid border-app-grey-4 bg-app-menu-color px-4 py-3 shadow-md", className)}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1 text-sm leading-5 text-app-grey-0">{message}</div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label={dismissLabel}
            className="rounded-lg border-0 bg-transparent p-1 text-app-grey-1 hover:text-app-grey-0"
          >
            <InlineSVG
              src="/assets/close.svg"
              className="w-4 h-4 [&_path]:fill-app-grey-1"
            />
          </button>
        )}
      </div>
      {actions.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          {actions.map((action, index) => (
            <SudoButton
              key={`${action.label}-${index}`}
              type="button"
              size="small"
              variant={action.variant ?? "tertiary"}
              onClick={action.onClick}
              disabled={action.disabled}
              aria-label={action.ariaLabel ?? action.label}
            >
              {action.label}
            </SudoButton>
          ))}
        </div>
      )}
    </div>
  );
};
